import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAbsoluteCoverUrl, getPlaceholderCover } from '../../utils/imageUtils';

// Single Promotional Banner
export const PromoBanner = ({ banner, className = '' }) => {
  const navigate = useNavigate();
  
  if (!banner) return null;
  
  return (
    <div 
      className={`bg-gradient-to-r ${banner.gradient} rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-pointer hover:opacity-95 transition-opacity ${className}`}
      onClick={() => navigate(banner.link)}
      data-testid={`promo-banner-${banner.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-4">
          <span className="text-3xl sm:text-4xl">{banner.icon}</span>
          <div>
            <h3 className="text-white font-bold text-base sm:text-lg md:text-xl">{banner.title}</h3>
            <p className="text-white/80 text-xs sm:text-sm">{banner.subtitle}</p>
          </div>
        </div>
        <button className="bg-white/20 hover:bg-white/30 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-colors">
          {banner.cta} →
        </button>
      </div>
    </div>
  );
};

// Promotional Banner Carousel/Grid
export const PromoBannerGrid = ({ banners = [], className = '' }) => {
  const navigate = useNavigate();
  
  const defaultBanners = [
    {
      id: "writing-contest",
      title: "Writing Contest",
      subtitle: "Win up to $1,500 in prizes!",
      cta: "Enter Now",
      link: "/contest",
      gradient: "from-purple-600 to-pink-600",
      icon: "🏆"
    },
    {
      id: "monthly-contest", 
      title: "Monthly Challenge",
      subtitle: "Follow & Earn 7 Coins!",
      cta: "Join",
      link: "/contest/monthly",
      gradient: "from-amber-500 to-orange-600",
      icon: "🎁"
    },
    {
      id: "become-author",
      title: "Become an Author",
      subtitle: "Share your stories and earn",
      cta: "Start Writing",
      link: "/author-center",
      gradient: "from-green-500 to-teal-600",
      icon: "✍️"
    },
    {
      id: "new-stories",
      title: "Fresh Reads",
      subtitle: "Discover newly published stories",
      cta: "Browse",
      link: "/new-stories",
      gradient: "from-blue-500 to-indigo-600",
      icon: "📖"
    }
  ];
  
  const displayBanners = banners.length > 0 ? banners : defaultBanners;
  
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${className}`} data-testid="promo-banner-grid">
      {displayBanners.map((banner) => (
        <div 
          key={banner.id}
          className={`bg-gradient-to-r ${banner.gradient} rounded-xl p-4 sm:p-5 cursor-pointer hover:scale-[1.02] transition-transform`}
          onClick={() => navigate(banner.link)}
          data-testid={`promo-grid-${banner.id}`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl sm:text-3xl">{banner.icon}</span>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm sm:text-base truncate">{banner.title}</h3>
              <p className="text-white/80 text-xs truncate">{banner.subtitle}</p>
            </div>
            <span className="text-white/70 text-lg">→</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Story Promotional Card
export const PromoStoryCard = ({ story, label, className = '' }) => {
  const navigate = useNavigate();
  
  if (!story) return null;
  
  return (
    <div 
      className={`relative overflow-hidden rounded-xl cursor-pointer group ${className}`}
      onClick={() => navigate(`/story/${story.id}`)}
      data-testid={`promo-story-${story.id}`}
    >
      <img
        src={getAbsoluteCoverUrl(story.cover)}
        alt={story.title}
        className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform group-hover:scale-105"
        onError={(e) => {
          e.target.src = getPlaceholderCover();
        }}
      />
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      
      {/* Label Badge */}
      {label && (
        <span className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-dark-accent text-white px-2 py-0.5 rounded text-[10px] sm:text-xs font-semibold">
          {label}
        </span>
      )}
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
        <h3 className="text-white font-bold text-sm sm:text-base line-clamp-2 mb-1">{story.title}</h3>
        <p className="text-white/70 text-xs sm:text-sm">{story.views?.toLocaleString() || 0} reads</p>
      </div>
    </div>
  );
};

// Featured Author Card
export const PromoAuthorCard = ({ author, className = '' }) => {
  const navigate = useNavigate();
  
  if (!author) return null;
  
  return (
    <div 
      className={`bg-dark-card rounded-xl p-4 sm:p-5 cursor-pointer hover:ring-2 hover:ring-dark-accent transition-all ${className}`}
      onClick={() => navigate(`/author/${author.id}`)}
      data-testid={`promo-author-${author.id}`}
    >
      <div className="flex items-center gap-3 sm:gap-4 mb-3">
        {/* Profile Picture */}
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-dark-bg border-2 border-dark-accent flex-shrink-0">
          {author.profile_picture ? (
            <img src={author.profile_picture} alt={author.display_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-dark-accent text-lg sm:text-xl font-bold">
              {author.display_name?.[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-dark-text font-bold text-sm sm:text-base truncate">{author.display_name}</h3>
          <p className="text-dark-text-secondary text-xs">
            {author.story_count} stories • {author.total_views?.toLocaleString() || 0} reads
          </p>
        </div>
      </div>
      
      {/* Top Stories */}
      {author.top_stories?.length > 0 && (
        <div className="flex gap-2 overflow-hidden">
          {author.top_stories.slice(0, 3).map((story) => (
            <img 
              key={story.id}
              src={getAbsoluteCoverUrl(story.cover)}
              alt={story.title}
              className="w-12 h-16 sm:w-14 sm:h-20 object-cover rounded"
              onError={(e) => {
                e.target.src = getPlaceholderCover();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Compact Contest Banner
export const ContestBanner = ({ className = '' }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className={`bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-pointer relative overflow-hidden ${className}`}
      onClick={() => navigate('/contest')}
      data-testid="contest-banner"
    >
      {/* Background decoration */}
      <div className="absolute -right-4 -top-4 text-6xl sm:text-8xl opacity-20 rotate-12">🏆</div>
      
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl sm:text-3xl">🏆</span>
          <span className="bg-white/20 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold">
            LIVE NOW
          </span>
        </div>
        <h3 className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-1">Writing Contest</h3>
        <p className="text-white/80 text-xs sm:text-sm mb-3 sm:mb-4">Win up to $1,500 in prizes!</p>
        <button className="bg-white text-purple-600 px-4 sm:px-6 py-2 rounded-lg font-bold text-xs sm:text-sm hover:bg-white/90 transition-colors">
          Enter Now →
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
